import { useState, type ReactNode, type CSSProperties } from "react";
import { useIsMobile } from "./useIsMobile";

/* ============================ Card ============================ */
export function Card({
  title,
  eyebrow,
  aside,
  children,
  style,
}: {
  title?: ReactNode;
  eyebrow?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  const isMobile = useIsMobile();
  return (
    <section style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-sm)", padding: isMobile ? "var(--space-4)" : "var(--space-6)", ...style }}>
      {(title || eyebrow || aside) && (
        <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "var(--space-5)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            {eyebrow && (
              <span style={{ fontFamily: "var(--font-ui)", fontWeight: "var(--weight-semibold)" as any, fontSize: "var(--text-xs)", letterSpacing: "var(--tracking-eyebrow)", textTransform: "uppercase", color: "var(--sacred-text)" }}>
                {eyebrow}
              </span>
            )}
            {title && (
              <h2 style={{ margin: 0, fontFamily: "var(--font-heading)", fontWeight: "var(--weight-semibold)" as any, fontSize: "var(--text-lg)", lineHeight: "var(--leading-snug)", color: "var(--text-primary)" }}>
                {title}
              </h2>
            )}
          </div>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

/* ============================ LiturgicalBadge ============================ */
const LIT_VARIANTS: Record<string, { fg: string; bg: string; ring: string; label: string }> = {
  vert: { fg: "var(--lit-vert-fg)", bg: "var(--lit-vert-bg)", ring: "var(--lit-vert-ring)", label: "Vert" },
  violet: { fg: "var(--lit-violet-fg)", bg: "var(--lit-violet-bg)", ring: "var(--lit-violet-ring)", label: "Violet" },
  blanc: { fg: "var(--lit-blanc-fg)", bg: "var(--lit-blanc-bg)", ring: "var(--lit-blanc-ring)", label: "Blanc" },
  rouge: { fg: "var(--lit-rouge-fg)", bg: "var(--lit-rouge-bg)", ring: "var(--lit-rouge-ring)", label: "Rouge" },
  rose: { fg: "var(--lit-rose-fg)", bg: "var(--lit-rose-bg)", ring: "var(--lit-rose-ring)", label: "Rose" },
};
export function LiturgicalBadge({ color = "vert", children }: { color?: string; children?: ReactNode }) {
  const v = LIT_VARIANTS[color] || LIT_VARIANTS.vert;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontFamily: "var(--font-ui)", fontWeight: "var(--weight-semibold)" as any, fontSize: "var(--text-xs)", letterSpacing: "var(--tracking-wide)", textTransform: "uppercase", color: v.fg, background: v.bg, border: `1px solid ${v.ring}`, borderRadius: "var(--radius-pill)", padding: "3px 11px 3px 9px" }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: v.fg, flexShrink: 0 }} />
      {children || v.label}
    </span>
  );
}

/* ============================ DayHeader ============================ */
export function DayHeader({ dayName, date, year, badge, team, color = "vert" }: { dayName: ReactNode; date?: string; year?: string; badge?: ReactNode; team?: ReactNode; color?: string }) {
  const isMobile = useIsMobile();
  const v = LIT_VARIANTS[color] || LIT_VARIANTS.vert; // tint the card with the liturgical colour
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px", background: `color-mix(in srgb, ${v.bg} 45%, var(--bg-raised))`, border: "1px solid var(--border)", borderLeft: `4px solid ${v.fg}`, borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-sm)", padding: isMobile ? "8px 12px" : "10px 16px" }}>
      <span style={{ fontFamily: "var(--font-heading)", fontWeight: "var(--weight-semibold)" as any, fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)", lineHeight: "var(--leading-snug)", overflowWrap: "anywhere", color: "var(--text-primary)" }}>{dayName}</span>
      <span style={{ flex: 1 }} />
      {date && <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{date}</span>}
      {year && <span style={{ fontFamily: "var(--font-ref)", fontSize: "var(--text-xs)", color: "var(--text-secondary)", background: "var(--bg-sunken)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "1px 7px" }}>Année {year}</span>}
      {badge}
      {team && (isMobile ? <div style={{ flexBasis: "100%", height: 0 }} /> : null)}
      {team}
    </div>
  );
}

/* ============================ ReadingBlock ============================ */
export function ReadingBlock({
  label,
  intro,
  reference,
  title,
  text,
}: {
  label?: string;
  intro?: string;
  reference?: string;
  title?: string;
  text?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {label && <span style={{ fontFamily: "var(--font-ui)", fontWeight: "var(--weight-semibold)" as any, fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>{label}</span>}
      <div style={{ fontFamily: "var(--font-body)", fontSize: "var(--text-base)", color: "var(--text-secondary)" }}>
        {intro}{" "}
        {reference && <span style={{ fontFamily: "var(--font-ref)", fontSize: "var(--text-sm)", color: "var(--accent-text)" }}>({reference})</span>}
      </div>
      {title && <div style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "var(--text-md)", color: "var(--text-primary)" }}>{title}</div>}
      {text && (
        <div>
          <button type="button" onClick={() => setOpen((o) => !o)} style={{ display: "inline-flex", alignItems: "center", gap: "6px", border: "none", background: "transparent", padding: "2px 0", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)" as any, color: "var(--accent-text)" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform var(--dur-fast) var(--ease-out)" }}><path d="m9 18 6-6-6-6" /></svg>
            {open ? "Masquer le texte" : "Voir le texte intégral"}
          </button>
          {open && (
            <div style={{ whiteSpace: "pre-wrap", fontFamily: "var(--font-serif)", fontSize: "var(--text-md)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)", background: "var(--bg-sunken)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: "14px 16px", marginTop: "8px", maxHeight: 300, overflow: "auto" }}>
              {text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================ Banner ============================ */
const TONES: Record<string, { fg: string; bg: string; ring: string }> = {
  info: { fg: "var(--accent-text)", bg: "var(--accent-soft)", ring: "var(--accent-ring)" },
  warning: { fg: "var(--sacred-text)", bg: "var(--sacred-soft)", ring: "var(--sacred-ring)" },
  danger: { fg: "var(--danger)", bg: "var(--danger-soft)", ring: "var(--danger-ring)" },
  neutral: { fg: "var(--text-secondary)", bg: "var(--bg-sunken)", ring: "var(--border)" },
};
const TONE_ICON: Record<string, ReactNode> = {
  info: <path d="M12 16v-4M12 8h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z" />,
  warning: <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01" />,
  danger: <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01" />,
  neutral: <path d="M12 16v-4M12 8h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z" />,
};
export function Banner({ tone = "info", children, action }: { tone?: "info" | "warning" | "danger" | "neutral"; children?: ReactNode; action?: ReactNode }) {
  const t = TONES[tone] || TONES.info;
  return (
    <div role="status" style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "10px", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: t.fg, background: t.bg, border: `1px solid ${t.ring}`, borderRadius: "var(--radius-md)", padding: "11px 14px" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
        {TONE_ICON[tone] || TONE_ICON.info}
      </svg>
      <div style={{ flex: "1 1 200px", minWidth: 0, lineHeight: "var(--leading-normal)" }}>{children}</div>
      {action}
    </div>
  );
}

/* ============================ Spinner ============================ */
export function Spinner({ size = 18, label }: { size?: number; label?: ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "10px", color: "var(--text-muted)", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)" }}>
      <span style={{ width: size, height: size, border: "2px solid var(--border-strong)", borderTopColor: "var(--accent)", borderRadius: "50%", display: "inline-block", animation: "adm-spin 0.8s linear infinite" }} />
      {label}
    </span>
  );
}
